import { IExportImportClient } from "@multiweb/clients/exportImport";
import { IAuthClient } from "./auth";
import { ClientType } from "./ClientType";
import { IContextClient } from "./context";
import { ICultureClient } from "./culture";
import { IDataEventsClient } from "./dataEvents";
import { IDataReaderClient } from "./dataReader";
import { IDocumentClient } from "./documents";
import { ILocalizationClient } from "./localization";
import { IMenuClient } from "./menu";
import { IModelDescriptionsClient } from "./modelDescriptions";
import { INotificationClient } from "./notifications";
import { ITaskClient } from "./tasks";
import { IUserClient } from "./user";
import { IUserSettingsClient } from "./userSettings";

/**
 * Tovární funkce vracející instanci klienta.
 * @includeToDoc
 */
export type FactoryFunction<T = unknown> = () => T;

type ClientTypeMap = {
    [ClientType.menu]: IMenuClient;
    [ClientType.user]: IUserClient;
    [ClientType.notifications]: INotificationClient;
    [ClientType.tasks]: ITaskClient;
    [ClientType.authentication]: IAuthClient;
    [ClientType.userSettings]: IUserSettingsClient;
    [ClientType.localizations]: ILocalizationClient;
    [ClientType.culture]: ICultureClient;
    [ClientType.dataReader]: IDataReaderClient;
    [ClientType.document]: IDocumentClient;
    [ClientType.dataEvents]: IDataEventsClient;
    [ClientType.modelDescriptions]: IModelDescriptionsClient;
    [ClientType.context]: IContextClient;
    [ClientType.exportImport]: IExportImportClient;
};

/**
 * Továrna pro vytváření klientů pro komunikaci s webovým API.
 * @includeToDoc
 */
export interface IClientFactory {
    /**
     * Zaregistruje implementaci komunikačního klienta.
     * @param clientType Typ komunikačního klienta
     * @param factory Tovární funkce vracející instanci klienta
     */
    register<K extends keyof ClientTypeMap>(clientType: K, factory: FactoryFunction<ClientTypeMap[K]>): void;
    register(clientType: string | ClientType, factory: FactoryFunction): void;

    /**
     * Získá instanci komunikačního klienta pro zadaný typ.
     * @param clientType Typ komunikačního klienta
     * @returns Instance komunikačního klienta
     */
    get<K extends keyof ClientTypeMap>(clientType: K): ClientTypeMap[K];
    get(clientType: string | ClientType): unknown;
}

export class IClientFactory {
    private static _methods: (keyof IClientFactory)[] = ["register", "get"];
    private static _properties: (keyof IClientFactory)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && !this._properties.some((value) => !Reflect.has(instance, value))
            && !this._methods.some(value => typeof instance[value] !== "function");
    }
}
